import React, { useEffect, useState } from 'react'
import { FlatList, RefreshControl, Platform } from 'react-native'
import { Amphora, Shirt, Handshake, Tag, Store, MapPin, Package, Settings, Plus, LucideIcon } from 'lucide-react-native'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '@fliisi/i18n'
import { supabase } from '../lib/supabase'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { Button, ButtonText, Card, EmptyState, Spinner, StatCard, StatusBadge, Text, TopBar, XStack, YStack } from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MyStores'>
}
interface StoreItem {
  id: string; name: string; store_type: string; status: string
  address_city: string | null; created_at: string
  products_count?: number; products_for_sale?: number
}

const STORE_TYPE_ICONS: Record<string, LucideIcon> = {
  antique_shop: Amphora, vintage_store: Shirt, consignment_store: Handshake, thrift_store: Tag,
}

export default function MyStoresScreen({ navigation }: Props) {
  const { user, signOut } = useAuth()
  const { t, language, setLanguage } = useLanguage()
  const [stores, setStores] = useState<StoreItem[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const fetchStores = async () => {
    if (!user) return
    setLoading(true)
    try {
      const { data: staffData } = await supabase
        .from('flea_market_staff').select('flea_market_id, role')
        .eq('user_id', user.id).in('role', ['owner', 'admin', 'manager'])
      if (!staffData?.length) { setStores([]); return }

      const storeIds = staffData.map(s => s.flea_market_id)
      const { data: storesData } = await supabase
        .from('flea_markets').select('id, name, store_type, status, address_city, created_at')
        .in('id', storeIds).in('store_type', ['antique_shop', 'vintage_store', 'consignment_store', 'thrift_store'])
        .order('created_at', { ascending: false })

      const withCounts = await Promise.all(
        (storesData || []).map(async store => {
          const { count: total } = await supabase.from('products').select('id', { count: 'exact', head: true }).eq('flea_market_id', store.id)
          const { count: forSale } = await supabase.from('products').select('id', { count: 'exact', head: true }).eq('flea_market_id', store.id).eq('status', 'for_sale')
          return { ...store, products_count: total || 0, products_for_sale: forSale || 0 }
        })
      )
      setStores(withCounts)
    } catch (e) { console.error('Error fetching stores:', e) }
    finally { setLoading(false); setRefreshing(false) }
  }

  useEffect(() => { fetchStores() }, [user])

  const renderStore = ({ item }: { item: StoreItem }) => {
    const IconComponent = STORE_TYPE_ICONS[item.store_type] || Store
    return (
      <Card variant="elevated" br="$xl" p="$md" mb="$md">
        <XStack ai="flex-start" mb="$md">
          <YStack w={56} h={56} br="$lg" bg="rgba(116,140,112,0.1)" jc="center" ai="center">
            <IconComponent size={32} color="#748C70" />
          </YStack>
          <YStack f={1} ml="$md">
            <Text preset="heading" fontSize={18}>{item.name}</Text>
            <Text preset="bodySmall" tone="secondary" mt={2}>{t(item.store_type) || item.store_type}</Text>
            {item.address_city && (
              <XStack ai="center" gap={4} mt={4}>
                <MapPin size={12} color="#858B80" />
                <Text preset="caption" tone="secondary">{item.address_city}</Text>
              </XStack>
            )}
          </YStack>
          <StatusBadge label={t(item.status) || item.status} variant={item.status === 'active' ? 'success' : 'warning'} />
        </XStack>
        <XStack gap="$sm" mb="$md">
          <StatCard f={1} label={t('for_sale') || 'For Sale'} value={String(item.products_for_sale || 0)} />
          <StatCard f={1} label={t('total') || 'Total'} value={String(item.products_count || 0)} />
        </XStack>
        <XStack gap="$sm">
          <Button variant="primary" f={1} onPress={() => navigation.navigate('StoreInventory', { storeId: item.id })}>
            <Package size={16} color="#fff" />
            <ButtonText ml="$xs">{t('inventory') || 'Inventory'}</ButtonText>
          </Button>
          <Button variant="secondary" onPress={() => navigation.navigate('StoreSettings', { storeId: item.id })}>
            <Settings size={18} color="#3E423A" />
          </Button>
        </XStack>
      </Card>
    )
  }

  return (
    <YStack f={1} bg="$background">
      <XStack jc="space-between" ai="center" px="$lg" pt={Platform.OS === 'web' ? 16 : 52} pb="$md" bg="$backgroundCard" borderBottomWidth={1} borderBottomColor="$borderLight">
        <Text preset="heading" fontSize={24} color="$primary">{t('my_stores') || 'My Stores'}</Text>
        <XStack gap="$sm">
          <Button variant="ghost" size="sm" onPress={() => setLanguage(language === 'fi' ? 'en' : 'fi')}>
            <ButtonText>{language.toUpperCase()}</ButtonText>
          </Button>
          <Button variant="ghost" size="sm" onPress={signOut}>
            <ButtonText color="$error">{t('sign_out') || 'Sign Out'}</ButtonText>
          </Button>
        </XStack>
      </XStack>

      {loading ? (
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      ) : (
        <FlatList
          data={stores}
          renderItem={renderStore}
          keyExtractor={item => item.id}
          contentContainerStyle={{ padding: 16, paddingBottom: 100 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchStores() }} />}
          ListEmptyComponent={
            <EmptyState
              icon={<Store size={48} color="#748C70" />}
              title={t('no_stores') || 'No stores yet'}
              description={t('create_store_hint') || 'Create your own second-hand store'}
              action={
                <Button variant="primary" onPress={() => navigation.navigate('CreateStore')}>
                  <Plus size={18} color="#fff" />
                  <ButtonText ml="$xs">{t('create_store') || 'Create Store'}</ButtonText>
                </Button>
              }
            />
          }
          ListFooterComponent={stores.length > 0 ? (
            <Button variant="outline" onPress={() => navigation.navigate('CreateStore')} mt="$sm">
              <Plus size={18} color="#748C70" />
              <ButtonText ml="$xs">{t('add_store') || 'Add Store'}</ButtonText>
            </Button>
          ) : null}
        />
      )}
    </YStack>
  )
}
